import React, { useState, useMemo } from 'react';
import {
    IconButton,
    Chip,
    Stack,
    Box,
    Typography,
    Avatar
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CommonTable from '../UI/Table/CommonTable';

/**
 * UserTable component for displaying and managing user data in Growth Sessions
 */
const UserTable = ({
    tableData = [],
    handleEditUser,
    handleCreateSession,
    isLoading = false,
    actionButtons = null
}) => {
    // State management
    const [selectedUser, setSelectedUser] = useState(null);

    // Memoized column definitions
    const columns = useMemo(() => [
        { key: 'serialNo', label: 'S.No', minWidth: 70, align: 'center' },
        { key: 'name', label: 'Name', minWidth: 150 },
        { key: 'email', label: 'Email', minWidth: 200 },
        { key: 'designation', label: 'Designation', minWidth: 130 },
        { key: 'experience_level', label: 'Experience', minWidth: 110 },
        { key: 'role', label: 'Role', minWidth: 100 },
        { key: 'status', label: 'Status', minWidth: 100 },
        { key: 'actions', label: 'Actions', minWidth: 120 }
    ], []);

    // Helper functions
    const handleStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active': return 'success';
            case 'inactive': return 'error';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    const handleRoleColor = (role) => {
        switch (role?.toLowerCase()) {
            case 'manager': return { bg: '#e3f2fd', color: '#1976d2' };
            case 'lead': return { bg: '#f3e5f5', color: '#7b1fa2' };
            case 'mentor': return { bg: '#fff8e1', color: '#f9a825' };
            case 'user': return { bg: '#e8f5e8', color: '#2e7d32' };
            default: return { bg: '#f5f5f5', color: '#666' };
        }
    };

    const handleRowSelect = (user) => {
        setSelectedUser(selectedUser?.id === user.id ? null : user);
    };

    // Cell rendering
    const renderCell = (columnKey, value, row, index) => {
        switch (columnKey) {
            case 'serialNo':
                return (
                    <Box sx={{ textAlign: 'center', fontFamily: 'Poppins', fontWeight: 500 }}>
                        {index + 1}
                    </Box>
                );

            case 'name':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#0a0068' }}>
                            <PersonIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Typography variant="body2" sx={{
                            fontWeight: 400,
                            color: '#333',
                            fontSize: '13px',
                            fontFamily: 'Poppins'
                        }}>
                            {value || 'N/A'}
                        </Typography>
                    </Box>
                );

            case 'email':
                return (
                    <Typography variant="body2" sx={{
                        fontWeight: 400,
                        color: '#333',
                        fontSize: '13px',
                        fontFamily: 'Poppins'
                    }}>
                        {value || 'N/A'}
                    </Typography>
                );

            case 'designation':
                return (
                    <Typography variant="body2" sx={{
                        fontWeight: 400,
                        color: '#333',
                        fontSize: '13px',
                        fontFamily: 'Poppins'
                    }}>
                        {value || 'N/A'}
                    </Typography>
                );

            case 'experience_level':
                return (
                    <Chip
                        label={`${value || 0} yrs`}
                        size="small"
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            fontSize: '0.7rem',
                            height: 24,
                            fontFamily: 'Poppins'
                        }}
                    />
                );

            case 'role':
                const roleStyle = handleRoleColor(value);
                return (
                    <Chip
                        label={value || 'N/A'}
                        size="small"
                        sx={{
                            bgcolor: roleStyle.bg,
                            color: roleStyle.color,
                            fontWeight: 600,
                            borderRadius: 2,
                            fontSize: '0.7rem',
                            height: 24,
                            fontFamily: 'Poppins'
                        }}
                    />
                );

            case 'status':
                return (
                    <Chip
                        label={value || 'N/A'}
                        size="small"
                        color={handleStatusColor(value)}
                        sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24,
                            fontFamily: 'Poppins'
                        }}
                    />
                );

            case 'actions':
                return (
                    <Stack direction="row" spacing={0.5}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditUser(row);
                            }}
                            sx={{
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <EditIcon sx={{ fontSize: 16, color: '#666' }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCreateSession(row);
                            }}
                            sx={{
                                padding: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <AssignmentIcon sx={{ fontSize: 16, color: '#666' }} />
                        </IconButton>
                    </Stack>
                );
            default:
                return value || 'N/A';
        }
    };

    return (
        <CommonTable
            columns={columns}
            data={tableData}
            renderCustomCell={renderCell}
            onRowClick={handleRowSelect}
            selectedRow={selectedUser}
            actionButtons={actionButtons}
            isLoading={isLoading}
            searchPlaceholder="Search users..."
        />
    );
};

export default UserTable;